import React from 'react';
import PropTypes from 'prop-types';
import { ScanStatusBadge } from './ScanStatusBadge';
import { TrashIcon } from './StatusIcons';
import { UiMessages } from '../uiMessages';

export function UploadItemCard({
  item,
  mediaSizeText,
  interactionLocked,
  isUploadBlockedByCaseStatus,
  transcriptAccept,
  presentation,
  onDescriptionChange,
  onMediaDelete,
  onTranscriptToggle,
  onTranscriptFileChange,
  onBrowseTranscript,
  onTranscriptDescriptionChange,
  onTranscriptDelete,
  setTranscriptInputRef
}) {
  const {
    isRejectedRow,
    mediaTone,
    transcriptTone,
    mediaDeleteButtonStyle,
    transcriptDeleteButtonStyle
  } = presentation;

  const canModifyUpload = !isUploadBlockedByCaseStatus;
  const hasTranscript = Boolean(item.transcriptEnabled);
  const showTranscriptToggle = hasTranscript === false;
  const hasTranscriptFile = Boolean(item.transcriptFile);
  const isMediaUploading = item.mediaUploadStatus === 'uploading';
  const isTranscriptUploading = item.transcriptUploadStatus === 'uploading';

  const cardClassName = isRejectedRow
    ? 'upload-item-card upload-item-card-virus'
    : 'upload-item-card';

  const mediaDescriptionContent = isMediaUploading ? (
    <div className="upload-spinner" aria-label="Uploading" />
  ) : (
    <input
      type="text"
      className="description-input"
      placeholder="Description (optional)"
      value={item.description}
      disabled={interactionLocked}
      onChange={(event) => onDescriptionChange(item.id, event.target.value)}
    />
  );

  const transcriptNameContent = hasTranscriptFile ? (
    <div className="file-name-truncate transcript-file-name">{item.transcriptFile.name}</div>
  ) : (
    <button
      type="button"
      className="browse-button transcript-browse-button"
      disabled={interactionLocked || isUploadBlockedByCaseStatus}
      onClick={onBrowseTranscript}
    >
      Browse transcript
    </button>
  );

  let transcriptInlineActionContent = <div className="file-inline-progress" aria-hidden="true" />;
  if (isTranscriptUploading) {
    transcriptInlineActionContent = <div className="upload-spinner" aria-label="Uploading" />;
  } else if (hasTranscriptFile) {
    transcriptInlineActionContent = (
      <input
        type="text"
        className="description-input"
        placeholder="Description (optional)"
        value={item.transcriptDescription}
        disabled={interactionLocked}
        onChange={(event) => onTranscriptDescriptionChange(item.id, event.target.value)}
      />
    );
  }

  const transcriptDeleteLabel = hasTranscriptFile
    ? `Remove transcript file ${item.transcriptFile.name}`
    : 'Remove transcript';

  return (
    <div className={cardClassName}>
      <div className="uploaded-file-row">
        <div className="file-main-content">
          <div className="file-top-row">
            <div className="file-heading-row">
              <div className="file-heading-content">
                <div className="file-name-truncate file-title">{item.mediaFile.name}</div>
                <div className="file-subtitle">{mediaSizeText}</div>
                <ScanStatusBadge tone={mediaTone} />
              </div>
            </div>

            <div className="file-inline-actions">
              {mediaDescriptionContent}
              {canModifyUpload ? (
                <button
                  className="icon-action-button file-delete-button"
                  type="button"
                  disabled={interactionLocked}
                  style={mediaDeleteButtonStyle}
                  onClick={() => onMediaDelete(item.id)}
                  aria-label={`Remove media file ${item.mediaFile.name}`}
                >
                  <TrashIcon />
                </button>
              ) : null}
            </div>
          </div>

          <div className="transcript-section">
            {showTranscriptToggle ? (
              <label className="transcript-inline-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={hasTranscript}
                  disabled={interactionLocked || isUploadBlockedByCaseStatus}
                  onChange={(event) => onTranscriptToggle(item.id, event)}
                />
                <span>{UiMessages.transcriptIncluded}</span>
              </label>
            ) : null}

            {hasTranscript ? (
              <div className="transcript-upload-row">
                <input
                  ref={setTranscriptInputRef}
                  type="file"
                  className="d-none"
                  accept={transcriptAccept}
                  disabled={interactionLocked || isUploadBlockedByCaseStatus}
                  onChange={(event) => onTranscriptFileChange(item.id, event)}
                />

                <div className="file-heading-row transcript-file-body">
                  <div className="file-heading-content">
                    <div className="transcript-file-name-row">
                      {transcriptNameContent}
                    </div>
                    {hasTranscriptFile ? (
                      <ScanStatusBadge tone={transcriptTone} />
                    ) : null}
                  </div>
                </div>

                <div className="file-inline-actions transcript-file-inline-actions">
                  {transcriptInlineActionContent}
                  {canModifyUpload ? (
                    <button
                      className="icon-action-button file-delete-button"
                      type="button"
                      disabled={interactionLocked}
                      style={transcriptDeleteButtonStyle}
                      aria-label={transcriptDeleteLabel}
                      onClick={() => onTranscriptDelete(item)}
                    >
                      <TrashIcon />
                    </button>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

const filePropType = PropTypes.shape({
  name: PropTypes.string
});

UploadItemCard.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    mediaFile: filePropType,
    mediaUploadStatus: PropTypes.string,
    description: PropTypes.string,
    transcriptEnabled: PropTypes.bool,
    transcriptFile: filePropType,
    transcriptUploadStatus: PropTypes.string,
    transcriptDescription: PropTypes.string
  }).isRequired,
  mediaSizeText: PropTypes.string.isRequired,
  interactionLocked: PropTypes.bool.isRequired,
  isUploadBlockedByCaseStatus: PropTypes.bool.isRequired,
  transcriptAccept: PropTypes.string.isRequired,
  presentation: PropTypes.shape({
    isRejectedRow: PropTypes.bool,
    mediaTone: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    transcriptTone: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    mediaDeleteButtonStyle: PropTypes.object,
    transcriptDeleteButtonStyle: PropTypes.object
  }).isRequired,
  onDescriptionChange: PropTypes.func.isRequired,
  onMediaDelete: PropTypes.func.isRequired,
  onTranscriptToggle: PropTypes.func.isRequired,
  onTranscriptFileChange: PropTypes.func.isRequired,
  onBrowseTranscript: PropTypes.func.isRequired,
  onTranscriptDescriptionChange: PropTypes.func.isRequired,
  onTranscriptDelete: PropTypes.func.isRequired,
  setTranscriptInputRef: PropTypes.func.isRequired
};
